import { useMemo, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Moon, Plane, Radar, RefreshCw, Satellite, Sun, Sunrise, Sunset } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { altitudeColor, formatAlt, type Aircraft } from '@/lib/aircraft';
import type { BodyState } from '@/lib/astro';
import type { SatPosition } from '@/lib/satellites';
import { compass, haversineKm, type GeoPoint } from '@/lib/geo';

interface ObjectsPanelProps {
  location: GeoPoint;
  bodies: BodyState[];
  satellites: SatPosition[];
  satsLoading: boolean;
  satsError: boolean;
  onRetrySats: () => void;
  satsEnabled: boolean;
  aircraft: Aircraft[];
  aircraftTotal: number;
  aircraftLoading: boolean;
  aircraftEnabled: boolean;
  timezone?: string;
  onFlyTo: (p: GeoPoint, zoom: number) => void;
  onSelectSat: (norad: string | null) => void;
  onSelectPlane: (hex: string | null) => void;
}

function fmtTime(d: Date | null | undefined, tz?: string): string {
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: tz,
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(d);
  }
}

function SectionTitle({ icon: Icon, children }: { icon: typeof Sun; children: ReactNode }) {
  return (
    <h3 className="flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className="size-3.5" />
      {children}
    </h3>
  );
}

function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <Card className="border-dashed">
      <CardContent className="px-4 py-5 text-center text-xs text-muted-foreground">
        {children}
      </CardContent>
    </Card>
  );
}

function RowButton({
  onClick,
  children,
  title,
}: {
  onClick: () => void;
  children: ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
    >
      {children}
    </button>
  );
}

export function ObjectsPanel({
  location,
  bodies,
  satellites,
  satsLoading,
  satsError,
  onRetrySats,
  satsEnabled,
  aircraft,
  aircraftTotal,
  aircraftLoading,
  aircraftEnabled,
  timezone,
  onFlyTo,
  onSelectSat,
  onSelectPlane,
}: ObjectsPanelProps) {
  const satsOverhead = useMemo(
    () => satellites.filter((s) => s.aboveHorizon).sort((a, b) => b.el - a.el).slice(0, 10),
    [satellites],
  );

  const nearestAircraft = useMemo(() => {
    return [...aircraft]
      .map((ac) => ({ ac, dist: haversineKm(location, ac) }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 12);
  }, [aircraft, location]);

  const sun = bodies.find((b) => b.name === 'Sun');
  const moon = bodies.find((b) => b.name === 'Moon');

  return (
    <div className="space-y-4">
      {/* Celestial bodies */}
      <section className="space-y-1.5">
        <SectionTitle icon={Sun}>Sun, Moon &amp; Planets</SectionTitle>
        <Card>
          <CardContent className="space-y-0.5 p-2">
            {bodies.map((b) => (
              <RowButton
                key={b.name}
                onClick={() => onFlyTo(b.sub, 4)}
                title={`Fly to the ${b.name} zenith point`}
              >
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: b.color, boxShadow: `0 0 6px 1px ${b.color}66` }}
                />
                <span className="w-20 shrink-0 truncate text-sm">{b.name}</span>
                <span className="ml-auto flex items-center gap-1 font-mono text-xs text-muted-foreground">
                  {b.alt >= 0 ? (
                    <ArrowUp className="size-3 text-emerald-400" />
                  ) : (
                    <ArrowDown className="size-3 text-sky-400" />
                  )}
                  {b.alt >= 0 ? '+' : ''}
                  {b.alt.toFixed(1)}°
                </span>
                <span className="w-16 shrink-0 text-right font-mono text-xs text-muted-foreground">
                  {b.az.toFixed(0)}° {compass(b.az)}
                </span>
              </RowButton>
            ))}
          </CardContent>
        </Card>

        {(sun || moon) && (
          <Card>
            <CardContent className="space-y-1.5 p-3 text-xs">
              {sun && (
                <div className="flex items-center gap-2">
                  <Sunrise className="size-3.5 text-amber-400" />
                  <span className="text-muted-foreground">Sunrise</span>
                  <span className="ml-auto font-mono">{fmtTime(sun.rise, timezone)}</span>
                  <Sunset className="ml-3 size-3.5 text-orange-400" />
                  <span className="text-muted-foreground">Sunset</span>
                  <span className="ml-auto font-mono">{fmtTime(sun.set, timezone)}</span>
                </div>
              )}
              {moon && (
                <div className="flex items-center gap-2">
                  <Moon className="size-3.5 text-slate-300" />
                  <span className="text-muted-foreground">Moon</span>
                  <span className="ml-auto truncate">
                    {moon.moonPhaseName} · {moon.moonIllum?.toFixed(0)}% lit
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </section>

      {/* Satellites overhead */}
      <section className="space-y-1.5">
        <SectionTitle icon={Satellite}>
          Satellites overhead
          {!satsLoading && satsEnabled && (
            <Badge variant="secondary" className="ml-auto font-mono text-[10px]">
              {satellites.filter((s) => s.aboveHorizon).length}
            </Badge>
          )}
        </SectionTitle>
        {!satsEnabled ? (
          <EmptyNote>Enable the satellite layer to track satellites.</EmptyNote>
        ) : satsLoading ? (
          <div className="space-y-1.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : satsError ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center gap-3 px-4 py-6 text-center">
              <p className="text-xs text-muted-foreground">
                Could not load satellite orbit data (TLE sources unreachable).
              </p>
              <Button variant="outline" size="sm" onClick={onRetrySats}>
                <RefreshCw className="size-3.5" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : satsOverhead.length === 0 ? (
          <EmptyNote>No tracked satellites above the horizon right now.</EmptyNote>
        ) : (
          <Card>
            <CardContent className="space-y-0.5 p-2">
              {satsOverhead.map((s) => (
                <RowButton
                  key={s.norad}
                  onClick={() => {
                    onSelectSat(s.norad);
                    onFlyTo({ lat: s.lat, lon: s.lon }, 4);
                  }}
                  title="Select and fly to satellite"
                >
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ background: s.isIss ? '#fb923c' : '#22d3ee' }}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {s.name}
                    {s.isIss && (
                      <Badge variant="secondary" className="ml-1.5 px-1 py-0 text-[9px]">
                        ISS
                      </Badge>
                    )}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {s.heightKm.toFixed(0)} km
                  </span>
                  <span className="w-20 shrink-0 text-right font-mono text-xs text-emerald-400">
                    +{s.el.toFixed(0)}° {compass(s.az)}
                  </span>
                </RowButton>
              ))}
            </CardContent>
          </Card>
        )}
      </section>

      {/* Aircraft nearby */}
      <section className="space-y-1.5">
        <SectionTitle icon={Plane}>
          Aircraft nearby
          {!aircraftLoading && aircraftEnabled && aircraftTotal > 0 && (
            <Badge variant="secondary" className="ml-auto font-mono text-[10px]">
              {aircraftTotal}
            </Badge>
          )}
        </SectionTitle>
        {!aircraftEnabled ? (
          <EmptyNote>Enable the aircraft layer to track flights.</EmptyNote>
        ) : aircraftLoading ? (
          <div className="space-y-1.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : nearestAircraft.length === 0 ? (
          <EmptyNote>
            No aircraft reported in this map area. Zoom in for denser coverage.
          </EmptyNote>
        ) : (
          <Card>
            <CardContent className="space-y-0.5 p-2">
              {nearestAircraft.map(({ ac, dist }) => (
                <RowButton
                  key={ac.hex}
                  onClick={() => {
                    onSelectPlane(ac.hex);
                    onFlyTo({ lat: ac.lat, lon: ac.lon }, 8);
                  }}
                  title="Select and fly to aircraft"
                >
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ background: altitudeColor(ac.altBaro) }}
                  />
                  <span className="min-w-0 flex-1 truncate font-mono text-sm">
                    {ac.flight || ac.hex.toUpperCase()}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {formatAlt(ac.altBaro)}
                  </span>
                  <span className="w-20 shrink-0 text-right font-mono text-xs text-muted-foreground">
                    {dist < 10 ? `${dist.toFixed(1)} km` : `${dist.toFixed(0)} km`}
                  </span>
                </RowButton>
              ))}
            </CardContent>
          </Card>
        )}
      </section>

      <p className="flex items-start gap-1.5 px-1 text-[11px] leading-relaxed text-muted-foreground">
        <Radar className="mt-0.5 size-3.5 shrink-0" />
        Aircraft data: adsb.lol / adsb.fi community ADS-B feeders. Satellites: CelesTrak TLEs,
        propagated locally.
      </p>
    </div>
  );
}
